import React, { useEffect, useRef } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import styles from './EditorExtension_Count.module.css';
import { useCCContext } from '../CC/CCContext';
import { CC } from '../CC/CC';
import * as ccUtils from '../CC/utils';
import { generateQuery, replaceBasketIdSubqueries, postWithCache } from '../CC/utils';

const cssStringToObject = (cssString) => {
  return cssString.split(';').reduce((acc, style) => {
    if (style.trim()) {
      let [property, value] = style.split(':').map(str => str.trim());

      // Convert kebab-case to camelCase
      property = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

      // Handle numeric values
      if (!isNaN(value) && value.trim() !== '') {
        value = Number(value);
      }

      acc[property] = value;
    }
    return acc;
  }, {});
};

export default Node.create({
  name: 'reactComponentCount',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      selected: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-selected') === 'true',
        renderHTML: (attributes) => ({
          'data-selected': attributes.selected
        }),
      },
      state: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-state'),
        renderHTML: (attributes) => ({
          'data-state': attributes.state
        }),
      },
      query: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-query'),
        renderHTML: (attributes) => ({
          'data-query': attributes.query
        }),
      },
      htmlStyle: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html-style'),
        renderHTML: (attributes) => ({
          'data-html-style': attributes.htmlStyle
        }),
      },
    }
  },
  parseHTML() {
    return [{ tag: 'Count' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['Count', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props) => {
  const ctx = useCCContext();
  const { cc, setCc, user, first, setSelectedRecord, setFirst, clearSearch } = ctx;
  const { selected, updateAttributes } = props;
  const { state, query, htmlStyle } = props.node.attrs;
  const [stateCount, setStateCount] = React.useState(0);
  const componentRef = useRef({});

  useEffect(() => {
    const fetchStateCount = async () => {
      if (state) {
        try {
          let stateCC = state ? JSON.parse(state) : new CC();
          let queryObj = generateQuery(stateCC, user);
          queryObj = await replaceBasketIdSubqueries(queryObj);
          const payload = {
            action: "get",
            command: "search",
            query: queryObj,
            range: "1-1",
            responseformat: "json"
          };
          const response = await postWithCache(process.env.REACT_APP_BASE_URL, payload, componentRef.current);
          setStateCount(response.request.count);
          console.log("AFTER2", queryObj, response.request.count);
        } catch (error) {
          console.error("Error fetching state count:", error);
        }
      }
    };
    fetchStateCount();
  }, [state]);

  useEffect(() => {
    const handleSelection = () => {
      updateAttributes({ selected: props.editor.isActive('reactComponentCount') });
    };
    props.editor.on('selectionUpdate', handleSelection);
    return () => {
      props.editor.off('selectionUpdate', handleSelection);
    };
  }, [props.editor, updateAttributes]);

  const handleClick = (event) => {
    if (!props.editor.isEditable) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('albumexternal')) {
        window.open(`${window.location.origin}${window.location.pathname}?state=${state}`, '_blank');
      } else {
        if (state) {
          try {
            const defaultCCObj = new CC();
            const startUPCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(state));
            setCc(startUPCCObj);
            setSelectedRecord(null);
            setFirst(1);
          } catch (error) {
            console.error("Error parsing state:", error);
          }
        } else {
          clearSearch();
        }
        ctx.setViewMode('list');
      }
    }
  };

  const handleDoubleClick = (event) => {
    console.log('handleDoubleClick');
    if (props.editor.isEditable) {
      event.stopPropagation();
      const { state, query, htmlStyle } = props.node.attrs;
      const customEvent = new CustomEvent('showInsertContentPopup', {
        detail: {
          extension: 'Count',
          props: { state, query, htmlStyle },
          pos: props.getPos(),
          node: props.node
        },
        bubbles: true,
        cancelable: true
      });
      event.target.dispatchEvent(customEvent);
    }
  };

  console.log(htmlStyle);
  console.log(JSON.stringify(cssStringToObject(htmlStyle)));

  const formattedCount = new Intl.NumberFormat('fr-FR').format(stateCount);

  return (
    <NodeViewWrapper as="span" className={`react-component ${selected ? 'selected' : ''}`}>
      <span
        className={styles.hyperlink}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{
          __html: `<span style="${htmlStyle}">${formattedCount}</span>`
        }}
      />
    </NodeViewWrapper>
  );
};
